import React, { useState, useEffect, useMemo } from 'react';
import { Edit2, Plus, X, ChevronDown } from 'lucide-react';
import { getGPS, getOpponents, getPlayers, getMatchDrills, createOpponent, updateOpponent, getScenarios, getLoadScenarios, getPlayerPositions } from '../../api';
import { useSeason, dateToSeason } from '../../SeasonContext';
import { computeSessionDurationMinutes, scaleFactorForDuration, computeMatchPerformanceRows } from './matchPerformanceShared';
import { useUrlParam } from '../../hooks/useUrlParam';

const POSITIONS = ['All', 'CB', 'FB', 'CDM', 'CAM', 'ST', 'W'];

const f0 = n => (n == null || isNaN(n) ? '—' : Math.round(n).toLocaleString());
const f1 = n => (n == null || isNaN(n) ? '—' : n.toFixed(1));
const fPct = n => (n == null || isNaN(n) ? '—' : Math.round(n) + '%');

// Averages a %-of-target field across players, skipping players with no
// value (no target/scenario match) instead of counting them as 0.
const avgPct = (arr, key) => {
  const vals = arr.map(p => p[key]).filter(v => v != null && !isNaN(v));
  return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
};

// Green at 100%, red at 60%, interpolating hue through yellow
const hmldBestColor = pct => {
  if (pct == null) return '#78716c';
  const h = Math.max(0, Math.min(120, ((pct - 60) / 40) * 120));
  return `hsl(${Math.round(h)}, 75%, 52%)`;
};

// ── Opponent avatar ────────────────────────────────────────────────────────────
function OppLogo({ name, logo_url, size = 64 }) {
  const [err, setErr] = useState(false);
  if (logo_url && !err) {
    return (
      <img
        src={logo_url} alt={name}
        onError={() => setErr(true)}
        style={{ width: size, height: size, objectFit: 'contain', borderRadius: 6 }}
      />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: 8,
      background: 'linear-gradient(135deg,#1e3a5f,var(--border))',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.4, fontWeight: 700, color: '#60a5fa',
    }}>
      {(name || '?')[0].toUpperCase()}
    </div>
  );
}

// ── SVG stacked bar chart ──────────────────────────────────────────────────────
function MatchChart({ data }) {
  if (!data.length) return (
    <div style={{ textAlign: 'center', color: 'var(--text-dimmed)', padding: 32, fontSize: 13 }}>No match data yet</div>
  );
  const W = 560, H = 130, PL = 44, PR = 16, PT = 24, PB = 36;
  const cW = W - PL - PR, cH = H - PT - PB;
  const maxVal = Math.max(...data.map(d => d.hid + d.expl), 1);
  const bW = Math.min(60, (cW / data.length) * 0.55);
  const gap = cW / data.length;

  const yTicks = [0, maxVal / 2, maxVal].map(v => ({
    v, y: PT + cH - (v / maxVal) * cH,
  }));

  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H + PB}`} style={{ overflow: 'visible', display: 'block' }}>
      {yTicks.map(({ v, y }) => (
        <g key={v}>
          <line x1={PL} y1={y} x2={W - PR} y2={y} stroke="var(--border)" strokeWidth={1} />
          <text x={PL - 6} y={y + 4} textAnchor="end" fill="var(--text-dimmed)" fontSize={9}>
            {v >= 1000 ? Math.round(v / 1000) + 'k' : Math.round(v)}
          </text>
        </g>
      ))}
      {data.map((d, i) => {
        const cx = PL + i * gap + gap / 2;
        const x = cx - bW / 2;
        const y0 = PT + cH;
        const hidH = (d.hid / maxVal) * cH;
        const explH = (d.expl / maxVal) * cH;
        const totalH = hidH + explH;
        return (
          <g key={i}>
            <rect x={x} y={y0 - hidH} width={bW} height={Math.max(hidH, 0)} fill="#38bdf8" rx={2} />
            <rect x={x} y={y0 - totalH} width={bW} height={Math.max(explH, 0)} fill="#1e40af" rx={2} />
            {d.hmld > 0 && (
              <text x={cx} y={y0 - totalH - 5} textAnchor="middle" fill="#f59e0b" fontSize={10} fontWeight="600">
                {Math.round(d.hmld)}
              </text>
            )}
            <text x={cx} y={y0 + 14} textAnchor="middle" fill="var(--text-secondary)" fontSize={9}>{d.date?.slice(5)}</text>
            <text x={cx} y={y0 + 25} textAnchor="middle" fill="var(--text-dimmed)" fontSize={8}>
              {d.label?.split(' ').slice(0, 2).join(' ')}
            </text>
          </g>
        );
      })}
      <g>
        <rect x={PL + 8} y={H + PB - 12} width={10} height={8} fill="#38bdf8" rx={1} />
        <text x={PL + 21} y={H + PB - 5} fill="var(--text-muted)" fontSize={9}>HID</text>
        <rect x={PL + 50} y={H + PB - 12} width={10} height={8} fill="#1e40af" rx={1} />
        <text x={PL + 63} y={H + PB - 5} fill="var(--text-muted)" fontSize={9}>Explosive</text>
        <text x={PL + 130} y={H + PB - 5} fill="#f59e0b" fontSize={9}>■ HMLD (top label)</text>
      </g>
    </svg>
  );
}

// ── Opponent management modal ──────────────────────────────────────────────────
function OpponentModal({ opponents, onAdd, onUpdate, onClose }) {
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', logo_url: '' });
  const [addForm, setAddForm] = useState({ name: '', logo_url: '' });
  const [saving, setSaving] = useState(false);

  const inp = { padding: '8px 11px', borderRadius: 7, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 }}>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, width: '100%', maxWidth: 480, maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 40px 80px rgba(0,0,0,0.6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px 16px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ color: 'var(--text-primary)', fontSize: 15, fontWeight: 700, margin: 0 }}>Manage Opponents</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', padding: 4, borderRadius: 6 }}><X size={18} /></button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, padding: '12px 24px 24px' }}>
          {opponents.map(opp => (
            <div key={opp.id} style={{ borderBottom: '1px solid var(--bg-app)', padding: '10px 0' }}>
              {editId === opp.id ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} style={inp} placeholder="Name" />
                  <input value={editForm.logo_url} onChange={e => setEditForm(f => ({ ...f, logo_url: e.target.value }))} style={inp} placeholder="Logo URL" />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button disabled={saving} onClick={async () => { setSaving(true); await onUpdate(opp.id, editForm); setEditId(null); setSaving(false); }}
                      style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button onClick={() => setEditId(null)} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <OppLogo name={opp.name} logo_url={opp.logo_url} size={32} />
                  <span style={{ flex: 1, color: 'var(--text-primary)', fontSize: 13 }}>{opp.name}</span>
                  <button onClick={() => { setEditId(opp.id); setEditForm({ name: opp.name, logo_url: opp.logo_url || '' }); }}
                    style={{ background: 'transparent', border: '1px solid var(--border-strong)', borderRadius: 7, color: 'var(--text-secondary)', cursor: 'pointer', padding: '5px 8px', display: 'flex' }}>
                    <Edit2 size={13} />
                  </button>
                </div>
              )}
            </div>
          ))}

          <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Add new</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} style={inp} placeholder="Opponent name" />
              <input value={addForm.logo_url} onChange={e => setAddForm(f => ({ ...f, logo_url: e.target.value }))} style={inp} placeholder="Logo URL (optional)" />
              <button
                disabled={saving || !addForm.name.trim()}
                onClick={async () => { setSaving(true); await onAdd(addForm); setAddForm({ name: '', logo_url: '' }); setSaving(false); }}
                style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: addForm.name.trim() ? '#2563eb' : 'var(--border)', color: addForm.name.trim() ? '#fff' : 'var(--text-dimmed)', cursor: addForm.name.trim() ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start' }}>
                <Plus size={14} /> Add Opponent
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function MatchPerformance({ user, playerRecord = null }) {
  const { selectedSeasons } = useSeason();
  const selfName = (playerRecord || user?.playerRecord)?.name?.toLowerCase();
  const [sessions, setSessions]       = useState([]);
  const [opponents, setOpponents]     = useState([]);
  const [players, setPlayers]         = useState([]);
  const [allMatchDrills, setAllMatchDrills] = useState([]);
  const [scenarios, setScenarios]         = useState([]);
  const [customTemplates, setCustomTemplates] = useState([]);
  const [positions, setPositions]         = useState([]);
  // session_id is numeric everywhere it's compared/consumed below; the URL always hands back a
  // string, so it's coerced at the boundary here rather than changing every s.id === selectedId
  // comparison downstream.
  const [selectedIdRaw, setSelectedIdRaw] = useUrlParam('session_id', '');
  const selectedId = selectedIdRaw === '' ? null : Number(selectedIdRaw);
  const setSelectedId = (id) => setSelectedIdRaw(id == null ? '' : id);
  const [loading, setLoading]             = useState(true);
  const [showOppModal, setShowOppModal]   = useState(false);
  const [sortKey, setSortKey]             = useState('hmld');
  const [sortDir, setSortDir]             = useState('desc');
  const [posFilter, setPosFilter]         = useUrlParam('position', 'All');

  useEffect(() => {
    Promise.all([getGPS(), getOpponents(), getPlayers(), getMatchDrills(), getScenarios(), getLoadScenarios(), getPlayerPositions()])
      .then(([gpsSessions, opps, pls, mDrills, scens, ct, posHistory]) => {
        const match = (gpsSessions || []).filter(s => s.session_type === 'match');
        setSessions(match);
        setOpponents(opps || []);
        setPlayers(pls || []);
        setAllMatchDrills(mDrills || []);
        setScenarios((scens || []).map(s => ({ ...s, startDate: s.start_date?.slice(0, 10), playerId: s.player_id })));
        setCustomTemplates(ct || []);
        setPositions((posHistory || []).map(p => ({ ...p, startDate: p.start_date?.slice(0, 10), playerId: p.player_id })));
        if (match.length > 0) setSelectedId(match[0].id);
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredSessions = useMemo(
    () => sessions.filter(s => {
      const d = s.session_date?.slice(0, 10);
      if (!d) return true;
      const season = dateToSeason(d);
      return !season || selectedSeasons.length === 0 || selectedSeasons.includes(season);
    }),
    [sessions, selectedSeasons],
  );

  // When season filter changes and current session is no longer visible, pick the latest
  useEffect(() => {
    if (!filteredSessions.length) { setSelectedId(null); return; }
    if (!filteredSessions.find(s => s.id === selectedId)) {
      setSelectedId(filteredSessions[0].id);
    }
  }, [filteredSessions]);

  const selectedSession = filteredSessions.find(s => s.id === selectedId) || null;

  // Drills for selected session (already filtered to match halves on server)
  const currentDrills = useMemo(
    () => allMatchDrills.filter(d => d.session_id === selectedId),
    [allMatchDrills, selectedId]
  );

  const sessionDurationMinutes = useMemo(() => computeSessionDurationMinutes(currentDrills), [currentDrills]);
  const scaleFactor = scaleFactorForDuration(sessionDurationMinutes);

  // Per-player aggregated data for selected session
  const playerData = useMemo(() => {
    const matchDate = selectedSession?.session_date?.slice(0, 10);
    return computeMatchPerformanceRows(currentDrills, players, matchDate, scenarios, customTemplates, positions);
  }, [currentDrills, players, scenarios, customTemplates, positions, selectedSession]);

  // Top 3 by HMLD
  const top3 = playerData.slice(0, 3);

  // Team HMLD this match, rescaled to a 90-minute equivalent for both longer AND shorter
  // matches (see scaleFactorForDuration) — a shortened friendly shouldn't read as a low HMLD
  // match just because it was 75 minutes, any more than an extra-time match should read as a
  // high one just because it ran long.
  const teamHMLD = playerData.reduce((s, p) => s + p.hmld, 0) * scaleFactor;

  // Best team HMLD ever across all match sessions (drill-level, same scale as player table)
  // — deliberately keeps the OLD one-sided scaling (only >119min matches get rescaled down),
  // NOT the new symmetric scaleFactorForDuration teamHMLD/chartData use below. Confirmed
  // 2026-08-02: applying the <90min scale-up here too let one very short internal squad match
  // ("Onderlinge wedstrijd", ~51 min, 2026-04-17) balloon from a real ~22.9k to a fabricated
  // ~40.5k, becoming the new "best ever" and dragging down every match's %-of-best comparison
  // — the historical record itself shouldn't move just because today's match got scaled
  // correctly; only the CURRENT match's own number (teamHMLD above) should reflect the new rule.
  const bestTeamHMLD = useMemo(() => {
    const bySessionDrills = {};
    allMatchDrills.forEach(d => {
      if (!bySessionDrills[d.session_id]) bySessionDrills[d.session_id] = [];
      bySessionDrills[d.session_id].push(d);
    });
    const vals = Object.values(bySessionDrills).map(drills => {
      const rawHmld = drills.reduce((s, d) => s + (d.hmld || 0), 0);
      const duration = computeSessionDurationMinutes(drills);
      const oldOneSidedFactor = duration > 119 ? 90 / duration : 1;
      return rawHmld * oldOneSidedFactor;
    });
    return vals.length ? Math.max(...vals) : 0;
  }, [allMatchDrills]);

  const hmldOfBest = bestTeamHMLD > 0 ? Math.round((teamHMLD / bestTeamHMLD) * 100) : null;

  // Chart data — last 4 match sessions, each rescaled to its own 90-minute equivalent (same
  // as the totals strip, both directions) so different match formats stay comparable session
  // to session.
  const chartData = useMemo(() => {
    return sessions.slice(0, 4).map(sess => {
      const sd = allMatchDrills.filter(d => d.session_id === sess.id);
      const opp = opponents.find(o => o.id === sess.opponent_id);
      const factor = scaleFactorForDuration(computeSessionDurationMinutes(sd));
      return {
        label: opp?.name || sess.session_title,
        date:  sess.session_date?.slice(0, 10),
        hid:   sd.reduce((s, d) => s + (d.hid  || 0), 0) * factor,
        expl:  sd.reduce((s, d) => s + (d.expl || 0), 0) * factor,
        hmld:  sd.reduce((s, d) => s + (d.hmld || 0), 0) * factor,
      };
    }).reverse();
  }, [sessions, allMatchDrills, opponents]);

  // Position filter only affects the table (rows + its own total row) — the
  // summary cards/chart above stay whole-team regardless of this filter.
  const filteredPlayerData = useMemo(
    () => posFilter === 'All' ? playerData : playerData.filter(p => p.pos === posFilter),
    [playerData, posFilter]
  );

  const sortedPlayerData = useMemo(() => {
    const arr = [...filteredPlayerData];
    return arr.sort((a, b) => {
      if (sortKey === 'name') {
        const cmp = (a.name || '').localeCompare(b.name || '');
        return sortDir === 'asc' ? cmp : -cmp;
      }
      const av = a[sortKey] ?? -Infinity;
      const bv = b[sortKey] ?? -Infinity;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
  }, [filteredPlayerData, sortKey, sortDir]);

  // Cumulative volume metrics (td/hid/sd/hmld/sa/expl) are rescaled to a 90-min
  // equivalent for matches longer than standard, so totals stay comparable
  // across formats; maxSpd is a peak value and mins/*Pct are already per-player
  // minute-normalized, so none of those are rescaled.
  const computeTotals = list => ({
    td:   list.reduce((s, p) => s + p.td,   0) * scaleFactor,
    hid:  list.reduce((s, p) => s + p.hid,  0) * scaleFactor,
    sd:   list.reduce((s, p) => s + p.sd,   0) * scaleFactor,
    hmld: list.reduce((s, p) => s + p.hmld, 0) * scaleFactor,
    sa:   list.reduce((s, p) => s + p.sa,   0) * scaleFactor,
    expl: list.reduce((s, p) => s + p.expl, 0) * scaleFactor,
    maxSpd: Math.max(...list.map(p => p.maxSpd), 0),
    maxSpdPlayer: list.length ? list.reduce((best, p) => p.maxSpd > (best?.maxSpd ?? -Infinity) ? p : best, null)?.name : null,
    mins: list.reduce((s, p) => s + p.mins, 0),
    tdPct:   avgPct(list, 'tdPct'),
    hidPct:  avgPct(list, 'hidPct'),
    sdPct:   avgPct(list, 'sdPct'),
    hmldPct: avgPct(list, 'hmldPct'),
    saPct:   avgPct(list, 'saPct'),
  });

  // Whole-team totals — feeds the summary strip/chart, unaffected by posFilter.
  const totals = useMemo(() => computeTotals(playerData), [playerData, scaleFactor]);
  // Table's own footer row — reflects whichever position is currently filtered.
  const filteredTotals = useMemo(() => computeTotals(filteredPlayerData), [filteredPlayerData, scaleFactor]);

  const handleAddOpp = async data => {
    const created = await createOpponent(data);
    setOpponents(prev => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
  };

  const handleUpdateOpp = async (id, data) => {
    const updated = await updateOpponent(id, data);
    setOpponents(prev => prev.map(o => o.id === id ? updated : o));
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const card   = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12 };
  const selStyle = { padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', cursor: 'pointer', fontFamily: 'inherit' };
  const navBtn = (active) => ({
    padding: '7px 14px', borderRadius: 8, border: `1px solid ${active ? '#3b82f6' : 'var(--border-strong)'}`,
    background: active ? '#3b82f6' : 'transparent', color: active ? '#fff' : 'var(--text-muted)',
    cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
  });
  const thStyle = { padding: '9px 10px', background: 'var(--bg-app)', color: 'var(--text-muted)', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', textAlign: 'right', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' };
  const thLeft  = { ...thStyle, textAlign: 'left' };
  const sortIndicator = key => sortKey === key ? (sortDir === 'desc' ? ' ▼' : ' ▲') : '';
  const handleSort = key => {
    if (sortKey === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortKey(key); setSortDir('desc'); }
  };
  const td0 = { padding: '10px 10px', fontSize: 13, color: 'var(--text-primary)', textAlign: 'right', borderBottom: '1px solid var(--bg-app)' };
  const td0L = { ...td0, textAlign: 'left' };
  const green  = { background: 'rgba(16,185,129,0.12)', color: '#6ee7b7' };
  // Table-cell variant: the headline-stat usage above (line ~496) sets green text on the
  // ordinary dark card background, which reads fine — but stamping that same light green text
  // onto this cell's own green-tinted background made the two blend together (Frederik: "hard
  // to read"). Keeps the green text (a plain --text-primary white was tried first and read as
  // too flat), just a brighter shade with more separation from the tint behind it — see
  // --text-on-green in index.css, which also handles the day theme's inverted case.
  const greenCell = { ...green, color: 'var(--text-on-green)' };
  const medals = ['🥇', '🥈', '🥉'];

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--text-dimmed)', fontSize: 14 }}>
      Loading match data…
    </div>
  );
  if (!filteredSessions.length) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 16, opacity: 0.5 }}>
      <svg width="64" height="64" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
      <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)' }}>No data for this season</h2>
      <p style={{ fontSize: 14, color: 'var(--text-muted)' }}>Change the season filter in Settings to view other data.</p>
    </div>
  );

  const opponent = selectedSession
    ? opponents.find(o => o.id === selectedSession.opponent_id) || null
    : null;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>

      {/* ── Top bar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <select value={selectedId || ''} onChange={e => setSelectedId(Number(e.target.value))} style={selStyle}>
            {filteredSessions.length === 0 && <option value="">No match sessions yet</option>}
            {filteredSessions.map(s => {
              const opp = opponents.find(o => o.id === s.opponent_id);
              return <option key={s.id} value={s.id}>{opp?.name || s.session_title}</option>;
            })}
          </select>
          <ChevronDown size={14} color="var(--text-muted)" style={{ marginLeft: -26, pointerEvents: 'none' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <select
            value={selectedId || ''}
            onChange={e => setSelectedId(Number(e.target.value))}
            style={selStyle}
          >
            {filteredSessions.map(s => (
              <option key={s.id} value={s.id}>{s.session_date?.slice(0, 10)}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', gap: 6, marginLeft: 8 }}>
          <button style={navBtn(true)}>In-match</button>
          <button style={navBtn(false)}>Matrix</button>
          <button style={navBtn(false)}>Ind. report</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <select value={posFilter} onChange={e => setPosFilter(e.target.value)} style={selStyle}>
            {POSITIONS.map(pos => <option key={pos} value={pos}>{pos === 'All' ? 'All positions' : pos}</option>)}
          </select>
        </div>
        {user?.isAdmin && (
          <div style={{ marginLeft: 'auto' }}>
            <button onClick={() => setShowOppModal(true)}
              style={{ padding: '7px 13px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
              Manage opponents
            </button>
          </div>
        )}
      </div>

      {sessions.length === 0 ? (
        <div style={{ ...card, padding: 48, textAlign: 'center', color: 'var(--text-dimmed)' }}>
          <p style={{ fontSize: 15, margin: 0 }}>No match sessions found.</p>
          <p style={{ fontSize: 13, marginTop: 8 }}>Upload a CSV containing "Match-First Half-full" or "Match-Second Half-full" drills — they will be auto-detected as match sessions.</p>
        </div>
      ) : (
        <>
          {/* ── Info panel ── */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>

            {/* Opponent */}
            <div style={{ ...card, padding: 20, display: 'flex', alignItems: 'center', gap: 16, minWidth: 200 }}>
              <OppLogo name={opponent?.name || selectedSession?.session_title || '?'} logo_url={opponent?.logo_url} size={64} />
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                  {opponent?.name || selectedSession?.session_title || '—'}
                </div>
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
                  {selectedSession?.session_date?.slice(0, 10) || '—'}
                </div>
              </div>
            </div>

            {/* Top 3 HMLD */}
            <div style={{ ...card, padding: 20, flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>Top 3 HMLD</div>
              {top3.length === 0 ? (
                <div style={{ color: 'var(--text-dimmed)', fontSize: 13 }}>No drill data for this match</div>
              ) : top3.map((p, i) => (
                <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 18 }}>{medals[i]}</span>
                  <span style={{ flex: 1, color: 'var(--text-primary)', fontSize: 13, fontWeight: 600 }}>{p.name}</span>
                  <span style={{ fontSize: 13, color: '#f59e0b', fontWeight: 700 }}>{f0(p.hmld)} m</span>
                </div>
              ))}
            </div>

            {/* HMLD % of best */}
            <div style={{ ...card, padding: 20, minWidth: 140, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8, textAlign: 'center' }}>
                HMLD % of<br />Best Game
              </div>
              <div style={{ fontSize: 48, fontWeight: 900, color: hmldBestColor(hmldOfBest), lineHeight: 1 }}>
                {hmldOfBest != null ? hmldOfBest + '%' : '—'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 6 }}>
                {f0(teamHMLD)} / {f0(bestTeamHMLD)} m
              </div>
            </div>
          </div>

          {/* ── Team totals strip (mirrors the table's total row, same width) ── */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {[
              { label: 'TD',   value: f0(totals.td),   pct: totals.tdPct },
              { label: 'HID',  value: f0(totals.hid),  pct: totals.hidPct },
              { label: 'SD',   value: f0(totals.sd),   pct: totals.sdPct },
              { label: 'HMLD', value: f0(totals.hmld), pct: totals.hmldPct },
              { label: 'SA',   value: f0(totals.sa),   pct: totals.saPct },
            ].map(t => (
              <div key={t.label} style={{ ...card, flex: 1, minWidth: 90, padding: '12px 10px', textAlign: 'center' }}>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }}>{t.label}</div>
                <div style={{ fontSize: 18, fontWeight: 800, ...(t.pct >= 100 ? { color: green.color } : {}) }}>{t.value}</div>
                <div style={{ fontSize: 12, fontWeight: 700, marginTop: 2, ...(t.pct >= 100 ? { color: green.color } : {}) }}>{fPct(t.pct)}</div>
              </div>
            ))}
            <div style={{ ...card, flex: 1, minWidth: 90, padding: '12px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }}>Max Spd</div>
              <div style={{ fontSize: 18, fontWeight: 800 }}>{f1(totals.maxSpd)}</div>
              {totals.maxSpdPlayer && (
                <div style={{
                  fontSize: 11, color: 'var(--text-dimmed)', marginTop: 2,
                  filter: (!!selfName && totals.maxSpdPlayer.toLowerCase() !== selfName) ? 'blur(5px)' : 'none',
                  userSelect: (!!selfName && totals.maxSpdPlayer.toLowerCase() !== selfName) ? 'none' : 'auto',
                }}>{totals.maxSpdPlayer}</div>
              )}
            </div>
          </div>

          {/* ── Player table ── */}
          <div style={{ ...card, marginBottom: 16, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
              <thead>
                <tr>
                  <th style={thLeft} onClick={() => handleSort('name')}>Player{sortIndicator('name')}</th>
                  <th style={thStyle} onClick={() => handleSort('td')}>TD{sortIndicator('td')}</th>
                  <th style={thStyle} onClick={() => handleSort('tdPct')}>TD%{sortIndicator('tdPct')}</th>
                  <th style={thStyle} onClick={() => handleSort('hid')}>HID &gt;19.8{sortIndicator('hid')}</th>
                  <th style={thStyle} onClick={() => handleSort('hidPct')}>HID%{sortIndicator('hidPct')}</th>
                  <th style={thStyle} onClick={() => handleSort('sd')}>SD &gt;25.2{sortIndicator('sd')}</th>
                  <th style={thStyle} onClick={() => handleSort('sdPct')}>SD%{sortIndicator('sdPct')}</th>
                  <th style={thStyle} onClick={() => handleSort('hmld')}>HMLD{sortIndicator('hmld')}</th>
                  <th style={thStyle} onClick={() => handleSort('hmldPct')}>HMLD%{sortIndicator('hmldPct')}</th>
                  <th style={thStyle} onClick={() => handleSort('sa')}>SA{sortIndicator('sa')}</th>
                  <th style={thStyle} onClick={() => handleSort('saPct')}>SA%{sortIndicator('saPct')}</th>
                  <th style={thStyle} onClick={() => handleSort('maxSpd')}>Max spd{sortIndicator('maxSpd')}</th>
                  <th style={thStyle} onClick={() => handleSort('mins')}>Mins{sortIndicator('mins')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedPlayerData.length === 0 ? (
                  <tr><td colSpan={13} style={{ ...td0, textAlign: 'center', color: 'var(--text-dimmed)', padding: 32 }}>
                    {playerData.length === 0 ? 'No match half drill data for this session' : `No ${posFilter} players in this match`}
                  </td></tr>
                ) : sortedPlayerData.map(p => {
                  const blurName = !!selfName && p.name?.toLowerCase() !== selfName;
                  return (
                  <tr key={p.name}>
                    <td style={td0L}>
                      <span style={{ fontWeight: 600, filter: blurName ? 'blur(5px)' : 'none', userSelect: blurName ? 'none' : 'auto' }}>{p.name}</span>
                      {p.pos && <span style={{ fontSize: 11, color: 'var(--text-dimmed)', marginLeft: 6 }}>{p.pos}</span>}
                    </td>
                    <td style={{ ...td0, ...(p.tdGreen   ? greenCell : {}) }}>{f0(p.td)}</td>
                    <td style={{ ...td0, ...(p.tdGreen   ? greenCell : {}) }}>{fPct(p.tdPct)}</td>
                    <td style={{ ...td0, ...(p.hidGreen  ? greenCell : {}) }}>{f0(p.hid)}</td>
                    <td style={{ ...td0, ...(p.hidGreen  ? greenCell : {}) }}>{fPct(p.hidPct)}</td>
                    <td style={{ ...td0, ...(p.sdGreen   ? greenCell : {}) }}>{f0(p.sd)}</td>
                    <td style={{ ...td0, ...(p.sdGreen   ? greenCell : {}) }}>{fPct(p.sdPct)}</td>
                    <td style={{ ...td0, ...(p.hmldGreen ? greenCell : {}) }}>{f0(p.hmld)}</td>
                    <td style={{ ...td0, ...(p.hmldGreen ? greenCell : {}) }}>{fPct(p.hmldPct)}</td>
                    <td style={{ ...td0, ...(p.saGreen   ? greenCell : {}) }}>{f0(p.sa)}</td>
                    <td style={{ ...td0, ...(p.saGreen   ? greenCell : {}) }}>{fPct(p.saPct)}</td>
                    <td style={td0}>{f1(p.maxSpd)}</td>
                    <td style={td0}>{f1(p.mins)}</td>
                  </tr>
                  );
                })}
                {filteredPlayerData.length > 0 && (
                  <tr style={{ background: 'var(--bg-subtle)' }}>
                    <td style={{ ...td0L, fontWeight: 700, color: 'var(--text-secondary)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      {posFilter === 'All' ? 'Team total' : `${posFilter} total`}
                    </td>
                    <td style={{ ...td0, fontWeight: 700 }}>{f0(filteredTotals.td)}</td>
                    <td style={{ ...td0, fontWeight: 700 }}>{fPct(filteredTotals.tdPct)}</td>
                    <td style={{ ...td0, fontWeight: 700 }}>{f0(filteredTotals.hid)}</td>
                    <td style={{ ...td0, fontWeight: 700 }}>{fPct(filteredTotals.hidPct)}</td>
                    <td style={{ ...td0, fontWeight: 700 }}>{f0(filteredTotals.sd)}</td>
                    <td style={{ ...td0, fontWeight: 700 }}>{fPct(filteredTotals.sdPct)}</td>
                    <td style={{ ...td0, fontWeight: 700, color: '#f59e0b' }}>{f0(filteredTotals.hmld)}</td>
                    <td style={{ ...td0, fontWeight: 700 }}>{fPct(filteredTotals.hmldPct)}</td>
                    <td style={{ ...td0, fontWeight: 700 }}>{f0(filteredTotals.sa)}</td>
                    <td style={{ ...td0, fontWeight: 700 }}>{fPct(filteredTotals.saPct)}</td>
                    <td style={{ ...td0, fontWeight: 700 }}>{f1(filteredTotals.maxSpd)}</td>
                    <td style={td0}>{f1(filteredTotals.mins)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Last 4 matches chart ── */}
          <div style={{ ...card, padding: '20px 24px' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 16 }}>
              Last 4 matches — HID + Explosive distance
            </div>
            <MatchChart data={chartData} />
          </div>
        </>
      )}

      {showOppModal && (
        <OpponentModal
          opponents={opponents}
          onAdd={handleAddOpp}
          onUpdate={handleUpdateOpp}
          onClose={() => setShowOppModal(false)}
        />
      )}
    </div>
  );
}
